// Guided tours (WS-49 track T4, build contract section 5): plays the tours of the settings package on the live map.
// A tour is a list of stops; a stop is a place or a view, a named camera move (js/moves/), a stay, and changes of
// tour-able settings. This module only sequences. The camera flies through main.js (`select`, `flyTo`, `home`), a
// move turns it during the stay, and every change of the map goes through `runtime.set(patch, { source: 'tour',
// ms })`: the tour layer of the settings. That layer is temporary: a key set to `undefined` leaves it, and every key
// leaves it when a tour ends or is stopped, so the preset's values return.
// The tour builder rework (owner 2026-10-31):
//   - Looking up is the map's own (headlook.js): a view that looks up (`lookUp`, or VIS-57's sky views that turned the
//     view itself) is flown to as an ordinary flight carrying the look-up (`flyTo` `up`), and `looksUp()` tells main.js
//     not to lower it while the tour shows that picture. There is no free pose any more; `sky` plays as Hold.
//   - The lens (Q2 A): a view's own, else the map's own lens (`lens`); a tour that ends flying home eases to the
//     opening view's lens (`homeLens`).
//   - The tour's own look (Q3 A): a tour begins in the map's own look, the same for every visitor (`ownLook`).
//   - The northern lights (Q4 A, Q13 A): a change of where they stand fades (out where they are, moved unseen, in at
//     the new place) unless the stop says `auroraMove: 'sweep'`; main.js reads `fade` as it reads `lapse`.
//   - Words (Q6 A): `stop` and `arrive` carry the stop's `caption` (its own, else its place's name and first sentence);
//     `stop` carries `secs` (flight + stay) for the progress line. `pause(on)` holds a tour where it is.
// A stop with `restore: true` gives back what it changed (the hour, the lights) over the flight to the next stop, and
// a stop with `stay: 'show'` stays as long as one loop of the night's display (aurora.cycle, read through `display`).
// (Tours follow an activity, 2026-11-01) A stop at an activity with `follow` plays Hold and, through `ride` (main.js ->
// lazy/rideview.js): `camera` = the map's camera keeps up with what moves there after the place flight (as a tap on its
// pin does); `inside` = the activity's own view (the card's "View the ride" / "Skate along") for the stay, its glide-in
// the travel. Nothing moving now: a plain place stop. The next flight, or the end of the tour, takes the camera back.
// The api is `ToursApi` in settings/typedefs.js; main.js calls `step(dt)` once per frame.
import { migrateTour, tourOwn, t } from './settings/index.js';
import { moveFor } from './moves/index.js';
import { createRig, easeInOut } from './moves/rig.js';
/** @typedef {import('./settings/typedefs.js').ToursDeps} ToursDeps */
/** @typedef {import('./settings/typedefs.js').ToursApi} ToursApi */
/** @typedef {import('./settings/typedefs.js').Tour} Tour */
/** @typedef {import('./settings/typedefs.js').Stop} Stop */

const STAY = { short: 4, medium: 8, long: 16 }; // seconds
const SMOOTH_MS = 1500;   // an `ease: 'smooth'` change, and the tour's values leaving when it ends
const PLACE_FLIGHT = 1.6; // seconds of the engine's own place flight (`select(key, true)`), as on main
const HOME_FLIGHT = 1.8;  // seconds of main.js's flight home
const CUT = 0.01;         // a flight this short is a cut (reduced motion, `travel: 0`)
const LENS_BACK = 0.8;    // seconds for the field of view to return after a tour
const LOOK_STAY = 0.05;   // VIS-78: the stay of a look-only preview (its time-lapse runs in a moment)
const BACK = 3;           // VIS-57 `restore`: the least seconds what a stop changed takes to go back
const SHOW_FALLBACK = 60; // VIS-57: seconds of a `stay: 'show'` stop on a map without a night's display
// Q3 A: the look a tour plays in, the map's own unless its first stop sets it on the way in
const LOOK = ['season.current', 'time.date', 'time.hour', 'aurora.on', 'aurora.level', 'aurora.bearing', 'aurora.elevation', 'aurora.auto', 'lights.cabins'];
const DEG = 180 / Math.PI;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const num3 = (a) => Array.isArray(a) && a.length === 3 && a.every(Number.isFinite);
const viewOk = (v) => !!v && num3(v.position) && num3(v.target) && v.position.some((p, i) => p !== v.target[i]);
const reduced = () => !!globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const wordy = (c) => !!c && typeof c === 'object' && (!!t(c.title) || !!t(c.text)); // a caption with words in some language
/**
 * WS-52: how far the clock runs from the hour `from` to the hour `to`. `forward` (a time-lapse): on in time, past
 * midnight when `to` is the earlier hour. Otherwise the short way round, which may be back (-12..12; half a day
 * runs on). main.js applies it (`time.hour`), and reads `tours.lapse` to know which of the two a change is.
 */
export const hourSpan = (from, to, forward) => { const d = (((to - from) % 24) + 24) % 24; return forward || d <= 12 ? d : d - 24; };
// Where the stop being played sits in a tour that was read again: the same stop, else the same place (the legacy
// tour is a list of places, and a season change may replace the list), else -1 = go on from the first stop.
const where = (stops, cur) => {
  const place = cur.at?.place, i = stops.findIndex((s) => s && s.id === cur.id && s.at?.place === place);
  return i >= 0 || !place ? i : stops.findIndex((s) => s?.at?.place === place);
};
// The stop a step at the same place belongs to (the last one before it with a place or a view); any other stop is its own.
const ownerOf = (stop, tour) => (stop.at?.same ? tour.stops.slice(0, tour.stops.indexOf(stop)).findLast((s) => !s?.at?.same) : stop);
const placeOf = (stop, tour) => { const s = ownerOf(stop, tour); return typeof s?.at?.place === 'string' ? s.at.place : null; };
// (Tours follow an activity) the `follow` of a stop at a place (a step at the same place: its stop's), else falsy. The
// engine reads `inside` and takes any other value as `camera` (validate.js refuses the rest).
const followOf = (stop, tour) => placeOf(stop, tour) && ownerOf(stop, tour).follow;

/**
 * @param {ToursDeps} deps
 * @returns {ToursApi}
 */
export function createTours({ runtime, places, select, flyTo, home, camera, controls, inSeason, track, display = () => null, lens: mapLens = null, homeLens = null, words = null, head = null, ride = null }) {
  // the look-up of the picture the tour shows (degrees, headlook.js): a view's, then what From A to B makes of it
  let up = 0;
  const rig = createRig(camera, controls, { get: () => up, set: (deg) => { up = Math.max(0, deg); head?.(up / DEG); } });
  const subs = { __proto__: null, start: new Set(), stop: new Set(), arrive: new Set(), end: new Set(), progress: new Set() };
  // VIS-82: what every stop shows: the place's card, the stop's short text (its caption, drawn by the page on
  // `arrive`), or nothing. One choice for the whole tour: a stop's own `card` field is not read. VIS-226: the choice
  // is the playing tour's own (`run.tour`), the preset's `tours.show` only for a tour without one.
  const ownOf = (tour, key) => tourOwn(tour, key, runtime.get);
  const cards = () => ownOf(run?.tour, 'show') === 'card';
  // VIS-226: in `stop` and `arrive`. A stop at no place in a tour that shows the card has no card: it shows its words
  const looks = (tour, stop) => { const show = ownOf(tour, 'show'); return { show: show === 'card' && stop && !placeOf(stop, tour) ? 'text' : show, textLook: ownOf(tour, 'textLook') }; };
  const emit = (type, e) => { for (const fn of [...subs[type]]) fn({ type, ...e }); };
  // WS-52: a preview says what it plays (bridge.js `TourProgress`): a stop flies in, it has arrived, the playback is
  // over (`reason`). A visitor's tour says nothing here.
  const say = (r, phase, seconds, reason) => {
    // VIS-78: a look-only preview is the editor's own and done at once: it says nothing
    if (!r.real && !r.look) for (const fn of subs.progress) fn({ tour: r.id, stop: reason ? null : r.stop.id ?? null, index: reason ? -1 : r.index, count: r.count, phase, seconds, ...(reason && { reason }) });
  };
  const held = new Set();   // keys the tour layer holds now
  const warned = new Set(); // changes the runtime refused (each is reported once)
  let run = null;  // the playback: { queue, qi, real, id, tour, stop, plan, still, index, count, trav, left, len, t, begun, ended, round, hold, look, caption, paused }
  let last = null;  // VIS-226: the visitor's (or screen's) tour that played last: what `own` reads between tours
  let started;      // VIS-226: the id `start` was last given (undefined = the playlist): what `restart` plays again
  let holding = false; // VIS-78: an editor preview that has played and keeps the map as its stop left it, until `stop`
  let lens = null; // a running change of the camera's field of view
  let base = 0;    // the field of view the tour found and gives back

  // ---- the library -------------------------------------------------------------------------------------------
  const ids = (key) => { const v = runtime.get(key); return Array.isArray(v) ? v : []; };
  // no id: the playlist; an empty playlist is the active tours in their order
  const queueOf = (id) => (id != null ? [id] : ids('tours.playlist').length ? ids('tours.playlist') : ids('tours.active'));
  function library() {
    const v = runtime.get('tours.library');
    if (!v || typeof v !== 'object') return {};
    if (!Array.isArray(v) && !Array.isArray(v.stops)) return v;
    const one = migrateTour(v); // the package's one tour (today's `resort.tour`), not wrapped in a library
    return one ? { [one.id]: one } : {};
  }
  /** @returns {Tour | null} */
  const tourOf = (id) => { const d = migrateTour(library()[id]); return d && Array.isArray(d.stops) ? Object.assign(d, { id }) : null; };
  // The stops that can be played now: the place exists and is in season, or the stop has a view. A `same` step
  // belongs to the stop before it and is skipped with it.
  function playable(tour) {
    const out = [];
    let ok = true;
    for (const s of tour.stops) {
      const at = s?.at || {};
      if (!at.same) ok = typeof at.place === 'string' ? places.has(at.place) && inSeason(at.place) : viewOk(at.view);
      if (ok) out.push(s);
    }
    return out;
  }
  const after = (tour, pos) => { const ok = new Set(playable(tour)); return tour.stops.find((s, i) => i > pos && ok.has(s)) || null; };

  // ---- changes of the map ------------------------------------------------------------------------------------
  // Q3 A (owner 2026-10-31): a tour begins in the map's own look, so every visitor sees the same film: each look key
  // its first stop does not set on the way in takes the map's value (runtime.base: without the visitor's presses, link
  // and memory), smoothly, on the way to that stop. A key already showing that value is left alone.
  function ownLook(stop, sets) {
    const all = runtime.base?.();
    if (!all || typeof all !== 'object') return;
    const own = new Map((Array.isArray(stop.changes) ? stop.changes : []).filter((c) => c?.phase === 'travel' && typeof c.set === 'string' && c.to !== undefined).map((c) => [c.set, c.to]));
    const patch = {};
    for (const k of LOOK) if (!own.has(k) && (held.has(k) || all[k] !== runtime.get(k))) patch[k] = all[k];
    // a season brings its own date (runtime.js seasonDated) unless the patch names one: the map's, or the stop's own
    if (own.has('season.current')) delete patch['time.date'];
    else if ('season.current' in patch) patch['time.date'] = own.has('time.date') ? own.get('time.date') : all['time.date'];
    if (Object.keys(patch).length) sets.set(SMOOTH_MS, { ...patch, ...sets.get(SMOOTH_MS) });
  }
  // One phase's changes of a stop, grouped by how long each takes. `fresh` = a tour begins (or begins again, or
  // ends): what the layer still holds leaves it in the same call, so no key is applied twice.
  function change(stop, phase, secs, fresh = false) {
    const sets = new Map(); // ms -> patch; -1 = a time-lapse, which takes the whole phase (WS-52: apart, so `lapse` says so)
    for (const c of Array.isArray(stop?.changes) ? stop.changes : []) {
      if (typeof c?.set !== 'string' || c.to === undefined || (c.phase === 'travel' ? 'travel' : 'stay') !== phase) continue;
      const ms = c.ease === 'cut' ? 0 : c.ease === 'lapse' ? -1 : SMOOTH_MS;
      sets.set(ms, { ...sets.get(ms), [c.set]: c.to });
    }
    if (fresh && stop) ownLook(stop, sets);
    if (fresh) {
      const set = new Set([...sets.values()].flatMap(Object.keys)), drop = [...held].filter((k) => !set.has(k));
      if (drop.length) sets.set(SMOOTH_MS, { ...Object.fromEntries(drop.map((k) => [k, undefined])), ...sets.get(SMOOTH_MS) });
      held.clear();
    }
    for (const [ms, patch] of sets) {
      // read by the applier of `time.hour` while it is called: a time-lapse runs forward in time (VIS-78: not in a
      // look-only preview, which only puts the hour there: an hour moved back must not run the clock round the day)
      api.lapse = ms < 0 && !run?.look;
      // Q4 A / Q13 A: read by the northern lights' applier: they fade to a new place in the sky (also when the tour's
      // values leave at its end), or sweep there when the stop says so
      api.fade = stop?.auroraMove !== 'sweep';
      const res = runtime.set(patch, { source: 'tour', ms: ms < 0 ? secs * 1000 : ms });
      api.lapse = api.fade = false;
      for (const k of Object.keys(patch)) if (patch[k] !== undefined) held.add(k);
      for (const r of res?.rejected || []) {
        if (!warned.has(r.key)) console.warn(`[tours] change of "${r.key}" skipped: ${r.reason}`);
        warned.add(r.key);
      }
    }
  }

  // ---- the camera --------------------------------------------------------------------------------------------
  function lensTo(fov, secs) {
    lens = fov > 0 && Math.abs(fov - camera.fov) > 0.01 ? { from: camera.fov, to: fov, secs: Math.max(CUT, secs), t: 0 } : null;
  }
  // Q2 A: the map's own lens (the package's, not what was on screen), else the one the tour found
  const ownLens = () => { const f = mapLens?.(); return f > 0 ? f : base; };
  // the tilt the controls allow (degrees above the horizon, looking down at the target)
  const tilt = (deg) => clamp(deg, 90 - (controls.maxPolarAngle ?? Math.PI) * DEG, 90 - (controls.minPolarAngle ?? 0) * DEG);
  // Starts the flight to a stop and returns how long it takes (seconds; 0 = a cut, `travel: 0`).
  function travel(stop, plan, still) {
    const at = stop.at;
    if (at.same) return 0; // a step at the same place: the camera stays where it is
    // `follow: 'inside'`: the activity's own view takes the camera and the lens (lazy/rideview.js); its glide in is the
    // travel. Nothing to ride (or `camera`): the map's follow is armed behind today's place flight (`at.view` unread)
    const follow = typeof at.place === 'string' && stop.follow, glide = follow === 'inside' && ride?.start(at.place);
    if (glide > 0) { lens = null; up = 0; select(cards() ? at.place : null); return still ? CUT : glide; }
    if (follow) ride?.follow(at.place);
    const view = !follow && viewOk(at.view) ? at.view : null, secs = still ? CUT : plan.travel;
    const flight = secs == null ? null : { duration: Math.max(CUT, secs) };
    lensTo(view?.fov > 0 ? view.fov : ownLens(), secs ?? PLACE_FLIGHT);
    up = 0;
    if (view) {
      select(null); // the card of the stop before; this stop's own card opens on arrival
      const [px, py, pz] = view.position, [tx, ty, tz] = view.target, d = Math.hypot(px - tx, py - ty, pz - tz);
      const want = Math.asin((py - ty) / d) * DEG, el = tilt(want), r = clamp(d, controls.minDistance ?? 0, controls.maxDistance ?? Infinity);
      const past = want < 0 && el - want > 0.05 ? el - want : 0; // degrees
      let x = px - tx, z = pz - tz, target = controls.target.clone().set(tx, ty, tz);
      // a view that looks up by itself, its target above the camera (VIS-57's sky views turned the view up): the camera
      // keeps its spot, the map pose under it looks down as far as the controls let it, and the look-up turns up the
      // rest. A view only flatter than the lowest tilt keeps its target and rises to that tilt, as before
      if (past) {
        if (Math.hypot(x, z) < 1e-6) { x = camera.position.x - controls.target.x; z = camera.position.z - controls.target.z; } // straight up: the heading on screen
        const az = Math.atan2(x, z), e = el / DEG;
        target = controls.target.clone().set(px - Math.sin(az) * Math.cos(e) * r, py - Math.sin(e) * r, pz - Math.cos(az) * Math.cos(e) * r);
      }
      up = Math.min(90, (view.lookUp > 0 ? view.lookUp : 0) + past);
      // the view as a flight, inside the zoom and tilt limits of the controls, carrying its look-up (main.js flyTo `up`)
      flyTo(target, r, { elevation: el, azimuth: Math.atan2(x, z), duration: flight?.duration ?? PLACE_FLIGHT, ...(up > 0 && { up: up / DEG }) });
    } else {
      // a place: the engine frames it and opens its card, exactly as a visitor's click does
      if (flight) select(at.place, true, flight); else select(at.place, true);
      if (!cards()) select(null);
    }
    return secs ?? PLACE_FLIGHT;
  }

  // ---- playback ----------------------------------------------------------------------------------------------
  // What comes after the stop being played: the tour's next stop, else the first stop of the next tour of the
  // playlist that has one (`pass` = a tour begins). The tour is read again each time: seasons and the library change.
  function upcoming(r) {
    const n = r.queue.length, cur = r.stop ? r.queue[r.qi]() : null, next = cur && after(cur, where(cur.stops, r.stop));
    if (next) return { tour: cur, stop: next, qi: r.qi, pass: false };
    const loop = r.real && (!!runtime.get('tours.loop') || (n === 1 && !!cur?.loop));
    for (let k = 1; k <= n; k++) {
      const qi = r.qi + k;
      if (qi >= n && !loop) break;
      const tour = r.queue[qi % n](), first = tour && after(tour, -1);
      if (first) return { tour, stop: first, qi: qi % n, pass: true };
    }
    return { tour: cur, stop: null, qi: r.qi, pass: false };
  }
  // Statistics (decided 2026-10-13, contract section 13): one playback counts each of its tours once, however many
  // rounds a loop plays. `tour_start` when the tour first begins; `tour_end` when its playback is over after a full
  // round: it ran to its end and the playback ended or moved on to another tour, or the visitor stopped a loop that
  // had gone round at least once.
  const count = (event, id) => {
    const seen = event === 'tour_start' ? run.begun : run.ended;
    if (run.counted && !seen.has(id)) { seen.add(id); track(event, { tour: id }); }
  };
  function advance() {
    const { tour, stop, qi, pass } = upcoming(run);
    if (run.stop && (pass || !stop)) { // it ran to its end
      run.round.add(run.id);
      if (!stop || qi !== run.qi) count('tour_end', run.id);
    }
    if (!stop) return close('finished', tour?.end || {});
    if (pass) {
      const other = qi !== run.qi, before = run.stop ? run.id : null;
      run.qi = qi; run.id = tour.id || '';
      // a playlist moves on to another tour: the one before is over (it ran to its end), the playback goes on.
      // A tour that starts again by itself (loop) has neither an end nor a new start.
      if (other && before !== null) emit('end', { tour: before, reason: 'finished' });
      if (!run) return; // a listener stopped it
      if (other) emit('start', { tour: run.id });
      if (!run) return; // a listener stopped it
      count('tour_start', run.id);
    }
    enter(tour, stop, pass);
  }
  // The point the stop's move turns round and heads for: `moveOptions.around` (a place, or a point picked in the
  // builder), else the place the stop is at (also when it has its own saved angle: the turn is round the building,
  // not round wherever the saved view's target happened to fall; a step at the same place: its stop's place), else
  // null: the middle of the view (the controls' target).
  function pivotOf(stop, tour) {
    const a = stop.moveOptions?.around;
    if (a && num3(a.point)) return { x: a.point[0], y: a.point[1], z: a.point[2] };
    const o = places.get(typeof a?.place === 'string' ? a.place : placeOf(stop, tour));
    return o?.anchor ? { x: o.anchor.x, y: (o.base ?? 0) + 2, z: o.anchor.z } : null;
  }
  // Q6 A (owner 2026-10-31): the words of a stop: its own caption when it has words, else, at a place (a step at the
  // same place: its stop's), the place's name and the first sentence of its card text (main.js `words`, in the
  // page's language); null = none
  function captionOf(stop, tour) {
    if (wordy(stop.caption)) return stop.caption;
    const key = placeOf(stop, tour), w = key && words ? words(key) : null;
    return wordy(w) ? w : null;
  }
  // VIS-57 `restore`: what a stop changes, as it was before the stop (held by the tour layer, or not: the value below)
  const snapshot = (stop) => new Map((Array.isArray(stop.changes) ? stop.changes : []).filter((c) => typeof c?.set === 'string').map((c) => [c.set, { held: held.has(c.set), to: runtime.get(c.set) }]));
  // ...given back over the flight to the next stop, apart from what that stop sets on the way in itself
  function giveBack(stop, secs) {
    const own = new Set((Array.isArray(stop.changes) ? stop.changes : []).filter((c) => c?.phase === 'travel').map((c) => c.set)), patch = {};
    for (const [k, was] of run.restore) if (!own.has(k)) patch[k] = was.held ? was.to : undefined;
    run.restore = null;
    if (!Object.keys(patch).length) return;
    api.lapse = false; // the hour goes back the short way round the clock
    api.fade = true; // the northern lights go back to their place unseen (Q4 A)
    runtime.set(patch, { source: 'tour', ms: Math.max(BACK, secs) * 1000 });
    api.fade = false;
    for (const k of Object.keys(patch)) if (patch[k] === undefined) held.delete(k);
  }
  // VIS-57 `stay: 'show'`: as long as one loop of the night's display. Its length stands in until the stop has arrived
  // (the loop ends where the display says so); without a display it is SHOW_FALLBACK seconds
  const showLength = () => { const d = display(); return d?.length > 0 ? d.length : SHOW_FALLBACK; };
  function enter(tour, stop, fresh) {
    const ok = playable(tour), still = reduced() || run.look;
    run.tour = tour; // VIS-226: before the flight, which reads its `show`
    if (run.real) last = tour;
    if (fresh) run.restore = null; // a tour begins: what the layer holds leaves it anyway
    rig.around(pivotOf(stop, tour));
    const plan = moveFor(followOf(stop, tour) ? 'still' : stop.move)(stop.moveOptions || {}, rig); // (a stop that follows an activity: the ride or the follow moves the camera, not a move)
    const show = stop.stay === 'show' && !run.look;
    // VIS-78 `look`: the editor shows what a stop changes, at once: the camera stays where it is and the stay is a moment
    const stay = run.look ? LOOK_STAY : show ? showLength() : Number.isFinite(stop.stay) ? Math.max(0, stop.stay) : Object.hasOwn(STAY, stop.stay) ? STAY[stop.stay] : STAY.medium;
    const trav = run.look ? 0 : travel(stop, plan, still);
    Object.assign(run, { stop, plan, still, index: ok.indexOf(stop), count: ok.length, trav, t: 0, show: show ? { end: null } : null,
      left: plan.hold ? stay : trav + stay, len: plan.hold ? Math.max(0, stay - trav) : stay, caption: captionOf(stop, tour) });
    if (!run.look && !stop.at?.same && run.restore) giveBack(stop, trav);
    if (!run.look && stop.restore === true) run.restore = snapshot(stop);
    change(stop, 'travel', trav, fresh);
    // `secs`: how long the stop plays (its flight and its stay), for the page's progress line
    emit('stop', { tour: run.id, stop, index: run.index, count: run.count, caption: run.caption, secs: run.left, ...looks(tour, stop) });
    if (run?.stop !== stop) return; // a listener stopped the tour, or moved it on
    if (trav <= 0) arrive(); else say(run, 'travel', trav); // (a cut, or a step at the same place, arrives after its `stop`)
  }
  function arrive() {
    const { stop } = run;
    run.trav = 0;
    // VIS-57: the loop playing now ends the stay; one about to end (less than 30 % of it left) gives way to the next
    const d = run.show && display();
    if (d?.on && Number.isInteger(d.n)) run.show.end = d.n + (1 - (d.u ?? 0) < 0.3 ? 2 : 1);
    if (!run.look && cards() && viewOk(stop.at.view) && typeof stop.at.place === 'string') select(stop.at.place, false); // a view of a place: its card (VIS-78: not in a look-only preview, which only changes the look)
    change(stop, 'stay', run.len);
    say(run, 'stay', run.len);
    emit('arrive', { tour: run.id, stop, index: run.index, count: run.count, caption: run.caption, ...looks(run.tour, stop) }); // VIS-82: the camera is there: the stop's short text may show
  }
  // The playback is over: the tour layer empties, the lens returns, and a tour that ran to its end may fly home
  // (Q14 B), easing to the opening view's lens on the way (Q2 A).
  function close(reason, end = {}) {
    const id = run.id, was = run;
    run = null;
    say(was, 'end', 0, reason);
    // VIS-78: a held preview keeps its changes and its camera until the editor stops it (or plays something else)
    holding = was.hold && reason === 'finished';
    if (holding) return emit('end', { tour: id, reason, held: true }); // VIS-82 `held`: what the stop shows stays too (a ride or a follow of its stop goes on: `halt` ends it)
    const homeward = end.action === 'home';
    ride?.leave(homeward); // the last stop's ride or follow: the flight home takes the pose, else the glide out of the seat
    change(null, '', 0, true);
    lensTo((homeward && homeLens?.()) || base, reduced() ? CUT : homeward ? HOME_FLIGHT : LENS_BACK);
    if (homeward) { select(null); home(reduced() ? { duration: CUT } : undefined); }
    emit('end', { tour: id, reason, ...(end.caption ? { caption: end.caption } : {}) });
  }
  const halt = () => {
    if (!run) { if (holding) { holding = false; ride?.leave(false); change(null, '', 0, true); lensTo(base, LENS_BACK); emit('end', { tour: '', reason: 'stopped' }); } return; }
    if (run.round.has(run.id)) count('tour_end', run.id); // stopped after a full round
    close('stopped');
  };
  // queue: one function per tour, which reads it again; real: a visitor's or a screen's tour (may loop), not the
  // editor's preview of one stop; auto (WS-73): started by the page itself (autoplay, a lobby screen's idle restart),
  // played for real but never counted. Only a real run that is not auto is counted.
  function begin(queue, real, { hold = false, look = false, auto = false } = {}) {
    const next = { queue, qi: -1, real, counted: real && !auto, id: '', stop: null, begun: new Set(), ended: new Set(), round: new Set(), hold: !real && hold, look: !real && look };
    if (!upcoming(next).stop) return false;
    const held = holding;
    holding = false; // a held preview hands its layer on: what the next one does not set leaves it as it begins
    halt();
    if (!held) base = lens ? lens.to : camera.fov;
    run = next;
    advance();
    return true;
  }

  // WS-52: a tour that is not in the library, as it plays from its stop `from` (an id; none = the first). That stop
  // looks as it does inside the tour: it flies to its own place (a step at the same place: the place and the card of
  // the stop it belongs to) and starts from what the stops before it leave on screen, their changes applied as a cut
  // while it flies in. `one` = that stop alone, and no flight home after it.
  function lead(doc, from, one) {
    const d = migrateTour(doc), all = d && Array.isArray(d.stops) ? playable(d) : [], i = from == null ? 0 : all.findIndex((s) => s.id === from), s = all[i];
    if (!s) return null;
    const own = all.slice(0, i + 1).findLast((x) => !x.at.same) || s, list = (x) => (Array.isArray(x.changes) ? x.changes : []);
    // VIS-57: a stop that gives back what it changed (`restore`) and has been left by then leaves nothing on screen
    const left = (x, j) => x.restore === true && all.slice(j + 1, i + 1).some((y) => !y.at.same);
    const first = { ...s, at: own.at, card: own.card, follow: own.follow, changes: [...all.slice(0, i).flatMap((x, j) => (left(x, j) ? [] : list(x))).map((c) => ({ ...c, phase: 'travel', ease: 'cut' })), ...list(s)] };
    return { ...d, id: d.id || '', loop: false, stops: one ? [first] : [first, ...all.slice(i + 1)], ...(one && { end: {} }) };
  }

  const api = {
    lapse: false, // WS-52: true while a time-lapse change is handed to the runtime (see `change`)
    fade: false,  // owner 2026-10-31 (Q4 A): true while a change the northern lights fade through is handed to the runtime
    /** WS-52: plays an unsaved tour as a preview (not counted, never loops; `tour-progress` says what it plays). False = nothing of it can be played. */
    play(doc, { from, one, hold, look } = {}) { const d = lead(doc, from, one); return !!d && begin([() => d], false, { hold, look }); },
    list: () => ids('tours.active').map((id) => tourOf(id)).filter(Boolean).map((d) => ({ id: d.id, name: d.name, stops: playable(d).length })),
    // no id: the playlist; an empty playlist is the active tours in their order. auto (WS-73): the page started it
    // (autoplay, idle restart, TV loop): it plays the same and counts nothing
    // `from` (a stop's id, with a tour's id): the tour taken up again at that stop, as `play` leads into one (a style's
    // tour bar: the visitor touched the map, then pressed play)
    start(id, { auto = false, from } = {}) {
      started = id ?? undefined;
      const queue = queueOf(id).map((i) => () => tourOf(i)), d = id != null && from != null && lead(tourOf(id), from);
      if (d) queue[0] = () => d;
      return begin(queue, true, { auto });
    },
    stop: halt,
    // VIS-226: the lobby screen's idle restart plays again what was last started (that tour, or the playlist)
    restart({ auto = false } = {}) { return api.start(started, { auto }); },
    // VIS-226: what a tour decides for itself, for the tour playing now, else the one that played last, else the first
    // that a start would play; with none of them the preset's old value (TOUR_OWN `from`), else the default
    own(key) { return ownOf(run?.tour || last || tourOf(queueOf(started)[0]), key); },
    // WS-60: a style's tour controls: the next stop now, or the stop before again (also while paused: it holds there)
    next() { if (run) run.left = 0; },
    back() { const t = run?.queue[run.qi](), s = t && playable(t)[run.index - 1]; if (s) enter(t, s); },
    // owner 2026-10-31: the tour screen's pause: a paused tour holds where it is (a flight under way still lands);
    // `pause(false)` plays on. A new tour, its end and a stop clear it
    pause(on) { if (run) run.paused = !!on; },
    paused: () => !!run?.paused,
    step(dt) {
      if (lens) {
        lens.t = Math.min(1, lens.t + dt / lens.secs);
        camera.fov = lens.from + (lens.to - lens.from) * easeInOut(lens.t);
        camera.updateProjectionMatrix();
        if (lens.t >= 1) lens = null;
      }
      if (!run) return;
      const paused = !!run.paused;
      run.left -= paused ? Math.min(dt, Math.max(0, run.trav)) : dt; // paused: the stay's clock stands, the flight's runs out
      if (!paused && run.show && run.trav <= 0) { // VIS-57: a stop that plays one loop of the night's display
        const d = display();
        if (run.show.end == null || !d?.on) { /* no loop to follow: its length stands */ } else if (d.n >= run.show.end || run.t > 3 * showLength() + 30) run.left = 0; else run.left = Math.max(run.left, dt);
      }
      if (run.left <= 0) return advance(); // as on main: the frame that moves on does not turn the camera
      if (run.trav > 0) {
        run.trav -= dt;
        if (run.trav > 0) return;
        arrive();
      }
      if (paused || !run) return;
      run.t += dt;
      if (!run.still) run.plan.step(dt, run.t, run.len);
    },
    /** The picture the tour shows looks up (headlook.js): main.js does not lower the look-up while it does. */
    looksUp: () => !!run?.stop && up > 0,
    // (a page piece that comes in mid-tour reads the words, what shows and the seconds left from here)
    current: () => (run?.stop ? { tour: run.id, stop: run.stop, index: run.index, count: run.count, caption: run.caption, secs: Math.max(0, run.left), ...looks(run.tour, run.stop) } : null),
    on(event, fn) { subs[event]?.add(fn); return () => { subs[event]?.delete(fn); }; },
    // the editor plays one stop on its own: flight, changes and stay; then the tour layer empties again
    preview(one, tour) {
      if (one?.at) begin([() => ({ ...tour, id: tour?.id || '', loop: false, stops: [one], end: { action: 'none', caption: null } })], false);
    },
  };
  return api;
}
